// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  pageNumber?: number;
  pageSize?: number;
  totalCount?: number;
}

export interface PagedApiResponse<T> extends ApiResponse<T[]> {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

// Auth Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  role: string;
  userId: number;
  fullName: string;
  email: string;
  profileImage?: string;
  subscriptionType: 'Free' | 'Monthly' | 'Yearly';
  isSubscriptionActive: boolean;
  subscriptionEndDate?: string;
  refreshToken?: string;
  isActive: boolean;
  expiresAt: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

// Mentor Work Experience Types
export interface MentorWorkExperienceResponse {
  id: number;
  companyName: string;
  position: string;
  description?: string;
  startDate: string;
  endDate?: string;
  isCurrentJob: boolean;
  industry?: string;
  achievements?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMentorWorkExperienceRequest {
  companyName: string;
  position: string;
  description?: string;
  startDate: string;
  endDate?: string;
  isCurrentJob: boolean;
  industry?: string;
  achievements?: string;
}

export interface UpdateMentorWorkExperienceRequest {
  companyName: string;
  position: string;
  description?: string;
  startDate: string;
  endDate?: string;
  isCurrentJob: boolean;
  industry?: string;
  achievements?: string;
}

// Mentor Certificate types
export interface MentorCertificateResponse {
  id: number;
  certificateName: string;
  issuingOrganization?: string;
  certificateNumber?: string;
  issueDate: string;
  expiryDate?: string;
  isExpired: boolean;
  credentialUrl?: string;
  description?: string;
  certificateImage?: string; // Base64 string
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMentorCertificateRequest {
  certificateName: string;
  issuingOrganization?: string;
  certificateNumber?: string;
  issueDate: string;
  expiryDate?: string;
  isExpired: boolean;
  credentialUrl?: string;
  description?: string;
  certificateImage?: string;
}

export interface UpdateMentorCertificateRequest {
  certificateName: string;
  issuingOrganization?: string;
  certificateNumber?: string;
  issueDate: string;
  expiryDate?: string;
  isExpired: boolean;
  credentialUrl?: string;
  description?: string;
  certificateImage?: string;
}

// Mentor Availability types
export interface MentorAvailabilityResponse {
  id: number;
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  notes?: string;
  isAvailable: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMentorAvailabilityRequest {
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  notes?: string;
  isAvailable: boolean;
}

export interface UpdateMentorAvailabilityRequest {
  startTime: string;
  endTime: string;
  dayOfWeek: string;
  notes?: string;
  isAvailable: boolean;
}

export interface BulkCreateAvailabilityRequest {
  startDate: string;
  endDate: string;
  startTime: string; // TimeSpan format: "08:00:00"
  endTime: string; // TimeSpan format: "17:00:00"
  daysOfWeek: string[]; // ["Monday", "Tuesday", etc.]
  slotDurationMinutes?: number; // default 60
  notes?: string;
  isAvailable: boolean;
}

// Bootcamp types
export interface BootcampResponse {
  categoryId?: number;
  categoryName?: string;
  id: number;
  title: string;
  description?: string;
  requirements?: string;
  startDate: string;
  endDate: string;
  maxParticipants: number;
  currentParticipants: number;
  price: number;
  status: string;
  isOnline: boolean;
  meetingLink?: string;
  image?: string; // Base64 string olarak gelecek
  createdAt: string;
  updatedAt?: string;
  applicationCount: number;
}

export interface BootcampApplicationResponse {
  id: number;
  bootcampId: number;
  bootcampTitle: string;
  menteeId: number;
  menteeName: string;
  menteeEmail: string;
  coverLetter?: string;
  status: string;
  mentorNotes?: string;
  appliedAt: string;
  reviewedAt?: string;
  isReviewMentee?: boolean;
  isReviewMentor?: boolean;
}

export interface UpdateBootcampApplicationRequest {
  status: string;
  mentorNotes?: string;
}

export interface MentorBookingResponse {
  id: number;
  mentorId: number;
  menteeId: number;
  startTime: string;
  endTime: string;
  notes?: string;
  status: string;
  meetingLink?: string;
  price: number;
  createdAt: string;
  updatedAt?: string;
  isReviewMentee?: boolean;
  isReviewMentor?: boolean;
  mentee: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
  };
  mentorAvailability?: {
    id: number;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  };
  mentorService?: {
    id: number;
    title: string;
    description?: string;
    price: number;
  };
}

// Mentor Service types
export interface MentorServiceResponse {
  id: number;
  serviceName: string;
  description?: string;
  price: number;
  durationInMinutes: number;
  serviceType: string;
  requirements?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMentorServiceRequest {
  serviceName: string;
  description?: string;
  price: number;
  durationInMinutes: number;
  serviceType: string;
  requirements?: string;
  isActive: boolean;
}

export interface UpdateMentorServiceRequest {
  serviceName: string;
  description?: string;
  price: number;
  durationInMinutes: number;
  serviceType: string;
  requirements?: string;
  isActive: boolean;
}

// Mentee Education types
export interface MenteeEducationResponse {
  id: number;
  schoolName: string;
  department: string;
  degree?: string;
  startDate: string;
  endDate?: string;
  isGraduated: boolean;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMenteeEducationRequest {
  schoolName: string;
  department: string;
  degree?: string;
  startDate: string;
  endDate?: string;
  isGraduated: boolean;
  description?: string;
}

export interface UpdateMenteeEducationRequest {
  schoolName: string;
  department: string;
  degree?: string;
  startDate: string;
  endDate?: string;
  isGraduated: boolean;
  description?: string;
}

// Mentee Project types
export interface MenteeProjectResponse {
  id: number;
  projectName: string;
  projectDescription?: string;
  githubUrl?: string;
  liveUrl?: string;
  technologies?: string;
  startDate?: string;
  endDate?: string;
  isCompleted: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMenteeProjectRequest {
  projectName: string;
  projectDescription?: string;
  githubUrl?: string;
  liveUrl?: string;
  technologies?: string;
  startDate?: string;
  endDate?: string;
  isCompleted: boolean;
}

export interface UpdateMenteeProjectRequest {
  projectName: string;
  projectDescription?: string;
  githubUrl?: string;
  liveUrl?: string;
  technologies?: string;
  startDate?: string;
  endDate?: string;
  isCompleted: boolean;
}

// Mentee Skill types
export interface MenteeSkillResponse {
  id: number;
  skillName: string;
  skillLevel?: string;
  experienceYears?: number;
  description?: string;
  createdAt: string;
  updatedAt?: string;
  approvalCount?: number;
  approvals?: SkillApprovalInfo[];
}

export interface SkillApprovalInfo {
  mentorId: number;
  mentorFirstName: string;
  mentorLastName: string;
  mentorEmail?: string;
  approvedAt: string;
}

export interface CreateMenteeSkillRequest {
  skillName: string;
  skillLevel?: string;
  experienceYears?: number;
  description?: string;
}

export interface UpdateMenteeSkillRequest {
  skillName: string;
  skillLevel?: string;
  experienceYears?: number;
  description?: string;
}

// Mentee Work Experience types
export interface MenteeWorkExperienceResponse {
  id: number;
  companyName: string;
  position: string;
  description?: string;
  startDate: string;
  endDate?: string;
  isCurrentJob: boolean;
  industry?: string;
  achievements?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMenteeWorkExperienceRequest {
  companyName: string;
  position: string;
  description?: string;
  startDate: string;
  endDate?: string;
  isCurrentJob: boolean;
  industry?: string;
  achievements?: string;
}

export interface UpdateMenteeWorkExperienceRequest {
  companyName: string;
  position: string;
  description?: string;
  startDate: string;
  endDate?: string;
  isCurrentJob: boolean;
  industry?: string;
  achievements?: string;
}

// Mentee Certificate types
export interface MenteeCertificateResponse {
  id: number;
  certificateName: string;
  issuingOrganization?: string;
  certificateNumber?: string;
  issueDate: string;
  expiryDate?: string;
  isExpired: boolean;
  credentialUrl?: string;
  description?: string;
  certificateImage?: string; // Base64 string
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMenteeCertificateRequest {
  certificateName: string;
  issuingOrganization?: string;
  certificateNumber?: string;
  issueDate: string;
  expiryDate?: string;
  isExpired: boolean;
  credentialUrl?: string;
  description?: string;
  certificateImage?: string; // Base64 string
}

export interface UpdateMenteeCertificateRequest {
  certificateName: string;
  issuingOrganization?: string;
  certificateNumber?: string;
  issueDate: string;
  expiryDate?: string;
  isExpired: boolean;
  credentialUrl?: string;
  description?: string;
  certificateImage?: string; // Base64 string
}

// Mentor Search types
export interface MentorSearchRequest {
  searchTerm?: string;
  category?: string;
  skill?: string;
  location?: string;
  company?: string;
  minPrice?: number;
  maxPrice?: number;
  minExperience?: number;
  maxExperience?: number;
  language?: string;
  hasAvailability?: boolean;
  /** createdAt | rating | hourlyRate | experienceYears | name */
  sortBy?: string;
  /** asc | desc */
  sortOrder?: string;
  page: number;
  pageSize: number;
}

export interface MentorSearchResponse {
  id: number;
  firstName: string;
  lastName: string;
  profileImage?: string;
  title?: string;
  bio?: string;
  languages?: string;
  hourlyRate?: number;
  experienceYears?: number;
  category?: string;
  email?: string;
  skills: string[];
  certificates: string[];
  workExperiences: string[];
  educations: string[];
  projects: string[];
  bootcamps: string[];
  services: string[];
  hasAvailability: boolean;
  rating?: number;
  reviewCount?: number;
  createdAt: string;
}

export interface MentorRatingResponse {
  rating?: number;
  reviewCount: number;
}

export interface MentorDetailResponse {
  id: number;
  firstName: string;
  lastName: string;
  profileImage?: string;
  title?: string;
  bio?: string;
  languages?: string;
  hourlyRate?: number;
  experienceYears?: number;
  category?: string;
  email?: string;
  phone?: string;
  website?: string;
  linkedIn?: string;
  github?: string;
  twitter?: string;
  educations: MentorEducationResponse[];
  projects: MentorProjectResponse[];
  skills: MentorSkillResponse[];
  workExperiences: MentorWorkExperienceResponse[];
  certificates: MentorCertificateResponse[];
  bootcamps: BootcampResponse[];
  services: MentorServiceResponse[];
  availabilities: MentorAvailabilityResponse[];
  rating?: number;
  reviewCount?: number;
  createdAt: string;
  favouriteMentees?: { id: number; firstName: string; lastName: string }[];
}

export interface MentorSearchFilters {
  categories: string[];
  skills: string[];
  locations: string[];
  languages: string[];
  companies: string[];
  minPrice: number;
  maxPrice: number;
  minExperience: number;
  maxExperience: number;
}

export interface PagedResult<T> {
  data: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

// Bootcamp Application Types
export interface BootcampApplicationRequest {
  coverLetter?: string;
}

export interface BootcampApplicationResponse {
  id: number;
  coverLetter?: string;
  status: string;
  mentorNotes?: string;
  appliedAt: string;
  reviewedAt?: string;
  bootcampId: number;
  menteeId: number;
  bootcamp: BootcampInfo;
  mentee: MenteeInfo;
}

export interface BootcampInfo {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  price: number;
  image?: string;
  mentor: MentorInfo;
}

export interface MentorInfo {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
}

export interface MenteeInfo {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
}

// Bootcamp Types
export interface BootcampSearchResponse {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  maxParticipants: number;
  currentParticipants: number;
  price: number;
  location: string;
  isOnline: boolean;
  image?: string;
  mentorId: number;
  mentorName: string;
  mentorTitle?: string;
  mentorProfileImage?: string;
  categoryId?: number;
  categoryName?: string;
  createdAt: string;
}

export interface BootcampDetailResponse {
  id: number;
  title: string;
  description: string;
  categoryId?: number;
  categoryName?: string;
  requirements: string;
  startDate: string;
  endDate: string;
  maxParticipants: number;
  currentParticipants: number;
  price: number;
  location: string;
  isOnline: boolean;
  meetingLink?: string;
  image?: string;
  mentorId: number;
  mentorName: string;
  mentorTitle?: string;
  mentorBio?: string;
  mentorProfileImage?: string;
  mentorSkills: string[];
  createdAt: string;
}

export interface BootcampFiltersResponse {
  categories: string[];
  locations: string[];
  minPrice: number;
  maxPrice: number;
}


export interface CreateBootcampRequest {
  title: string;
  description: string;
  requirements: string;
  startDate: string;
  endDate: string;
  maxParticipants: number;
  price: number;
  status: string;
  isOnline: boolean;
  location?: string;
  meetingLink?: string;
  categoryId: number;
}

export interface UpdateBootcampRequest extends CreateBootcampRequest {}

export interface User {
  id: number;
  email: string;
  name: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  profileImage?: string;
  role: 'Admin' | 'Mentor' | 'Mentee' | 'Company';
  subscriptionType?: 'Free' | 'Monthly' | 'Yearly';
  isSubscriptionActive?: boolean;
  subscriptionEndDate?: string;
  // Company specific properties
  companyName?: string;
  address?: string;
  taxNumber?: string;
  authorizedPerson?: string;
  phoneNumber?: string;
  website?: string;
  companySize?: string;
  industry?: string;
  isApproved?: boolean;
}

// Mentee Types
export interface Mentee {
  id: number;
  name: string;
  email: string;
  phone?: string;
  profile?: MenteeProfile;
  skills?: MenteeSkill[];
  education?: MenteeEducation[];
  workExperience?: MenteeWorkExperience[];
  projects?: MenteeProject[];
  certificates?: MenteeCertificate[];
  references?: MenteeReference[];
}

export interface MenteeProfile {
  id: number;
  menteeId: number;
  bio?: string;
  website?: string;
  linkedin?: string;
  github?: string;
  profilePicture?: string;
}

export interface MenteeSkill {
  id: number;
  menteeId: number;
  name: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Expert';
}

export interface MenteeEducation {
  id: number;
  menteeId: number;
  institution: string;
  degree: string;
  fieldOfStudy: string;
  startDate: Date;
  endDate?: Date;
  description?: string;
}

export interface MenteeWorkExperience {
  id: number;
  menteeId: number;
  company: string;
  position: string;
  startDate: Date;
  endDate?: Date;
  description?: string;
}

export interface MenteeProject {
  id: number;
  menteeId: number;
  name: string;
  description: string;
  technologies: string[];
  startDate: Date;
  endDate?: Date;
  url?: string;
}

export interface MenteeCertificate {
  id: number;
  menteeId: number;
  name: string;
  issuer: string;
  issueDate: Date;
  expiryDate?: Date;
  credentialId?: string;
}

export interface MenteeReference {
  id: number;
  menteeId: number;
  name: string;
  position: string;
  company: string;
  email: string;
  phone?: string;
}

// Mentor Types
export interface Mentor {
  id: number;
  name: string;
  email: string;
  phone?: string;
  profile?: MentorProfile;
  services?: MentorService[];
  availability?: MentorAvailability[];
  bookings?: MentorBooking[];
}

export interface MentorProfile {
  id: number;
  mentorId: number;
  bio?: string;
  website?: string;
  linkedin?: string;
  github?: string;
  profilePicture?: string;
  hourlyRate: number;
}

export interface MentorService {
  id: number;
  mentorId: number;
  name: string;
  description: string;
  price: number;
  duration: number; // minutes
}

export interface MentorAvailability {
  id: number;
  mentorId: number;
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  startTime: string; // HH:mm format
  endTime: string; // HH:mm format
}

export interface MentorBooking {
  id: number;
  mentorId: number;
  menteeId: number;
  serviceId: number;
  startTime: Date;
  endTime: Date;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  notes?: string;
}

// Company Types
export interface Company {
  id: number;
  name: string;
  email: string;
  phone?: string;
  website?: string;
  description?: string;
  jobPostings?: JobPosting[];
}

export interface JobPosting {
  id: number;
  companyId: number;
  title: string;
  description: string;
  requirements: string[];
  location: string;
  salaryMin?: number;
  salaryMax?: number;
  employmentType: 'full-time' | 'part-time' | 'contract' | 'internship';
  status: 'active' | 'closed';
  applications?: JobApplication[];
}

export interface JobApplication {
  id: number;
  jobPostingId: number;
  menteeId: number;
  coverLetter?: string;
  status: 'pending' | 'reviewed' | 'accepted' | 'rejected';
  appliedAt: Date;
}

// Common Types
export interface Category {
  id: number;
  name: string;
  description?: string;
}


// Mentor Types

// Mentor Profile Types
export interface MentorProfileResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  websiteUrl?: string;
  company?: string;
  city?: string;
  country?: string;
  jobTitle?: string;
  hourlyRate: number;
  categoryId?: number;
  categoryName?: string;
  bio?: string;
  linkedInProfile?: string;
  githubProfile?: string;
  twitterProfile?: string;
  profileImage?: string; // Base64 encoded
  subscriptionType: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  isSubscriptionActive: boolean;
  isAvailable: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  educations: MentorEducationResponse[];
  projects: MentorProjectResponse[];
  skills: MentorSkillResponse[];
  workExperiences: MentorWorkExperienceResponse[];
  certificates: MentorCertificateResponse[];
  references: MentorReferenceResponse[];
}

export interface UpdateMentorProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  websiteUrl?: string;
  company?: string;
  city?: string;
  country?: string;
  jobTitle?: string;
  hourlyRate: number;
  categoryId?: number;
  bio?: string;
  linkedInProfile?: string;
  githubProfile?: string;
  twitterProfile?: string;
}

export interface MentorEducationResponse {
  id: number;
  schoolName: string;
  department: string;
  degree?: string;
  startDate: string;
  endDate?: string;
  isGraduated: boolean;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMentorEducationRequest {
  schoolName: string;
  department: string;
  degree?: string;
  startDate: string;
  endDate?: string;
  isGraduated: boolean;
  description?: string;
}

export interface UpdateMentorEducationRequest {
  schoolName: string;
  department: string;
  degree?: string;
  startDate: string;
  endDate?: string;
  isGraduated: boolean;
  description?: string;
}

// Mentor Project Types
export interface MentorProjectResponse {
  id: number;
  projectName: string;
  projectDescription?: string;
  githubUrl?: string;
  liveUrl?: string;
  technologies?: string;
  startDate?: string;
  endDate?: string;
  isCompleted: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMentorProjectRequest {
  projectName: string;
  projectDescription?: string;
  githubUrl?: string;
  liveUrl?: string;
  technologies?: string;
  startDate?: string;
  endDate?: string;
  isCompleted: boolean;
}

export interface UpdateMentorProjectRequest {
  projectName: string;
  projectDescription?: string;
  githubUrl?: string;
  liveUrl?: string;
  technologies?: string;
  startDate?: string;
  endDate?: string;
  isCompleted: boolean;
}

// Mentor Skill Types
export interface MentorSkillResponse {
  id: number;
  skillName: string;
  skillLevel?: string;
  experienceYears?: number;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateMentorSkillRequest {
  skillName: string;
  skillLevel?: string;
  experienceYears?: number;
  description?: string;
}

export interface UpdateMentorSkillRequest {
  skillName: string;
  skillLevel?: string;
  experienceYears?: number;
  description?: string;
}




export interface MentorReferenceResponse {
  id: number;
  referenceName: string;
  referencePosition?: string;
  referenceCompany?: string;
  referenceEmail?: string;
  referencePhone?: string;
  relationship?: string;
}

// Category Types
export interface CategoryResponse {
  id: number;
  name: string;
  description?: string;
  categoryImage?: string; // Base64 encoded
}

// Mentor Education Request Types


// Mentee Profile Types
export interface MenteeProfileResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  city?: string;
  country?: string;
  websiteUrl?: string;
  bio?: string;
  linkedInProfile?: string;
  githubProfile?: string;
  twitterProfile?: string;
  profileImage?: string; // Base64 encoded
  subscriptionType: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  isSubscriptionActive: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  educations: MenteeEducationResponse[];
  projects: MenteeProjectResponse[];
  skills: MenteeSkillResponse[];
  workExperiences: MenteeWorkExperienceResponse[];
  certificates: MenteeCertificateResponse[];
  references: MenteeReferenceResponse[];
}

export interface UpdateMenteeProfileRequest {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  city?: string;
  country?: string;
  websiteUrl?: string;
  bio?: string;
  linkedInProfile?: string;
  githubProfile?: string;
  twitterProfile?: string;
}



export interface MenteeSkillResponse {
  id: number;
  name: string;
  level: string;
  yearsOfExperience?: number;
}

export interface MenteeWorkExperienceResponse {
  id: number;
  company: string;
  position: string;
  startDate: string;
  endDate?: string;
  isCurrentlyWorking: boolean;
  description?: string;
}

export interface MenteeCertificateResponse {
  id: number;
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  credentialId?: string;
}

export interface MenteeReferenceResponse {
  id: number;
  name: string;
  position?: string;
  company?: string;
  email?: string;
  phone?: string;
}

// Mentor Booking Types
export interface MentorBookingRequest {
  mentorId: number;
  menteeId: number;
  startTime: string;
  endTime: string;
  notes?: string;
  price: number;
}

export interface MentorBookingResponse {
  id: number;
  startTime: string;
  endTime: string;
  notes?: string;
  status: string;
  meetingLink?: string;
  price: number;
  createdAt: string;
  updatedAt?: string;
  mentorId: number;
  menteeId: number;
  mentorAvailabilityId?: number;
  mentorServiceId?: number;
  mentor: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
  };
  mentee: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profileImage?: string;
  };
}

// Mentee Search Types
export interface MenteeSearchRequest {
  page: number;
  pageSize: number;
  searchTerm?: string;
  category?: string;
  skill?: string;
  approvedSkill?: string;
  project?: string;
  bootcampTopic?: string;
  mentorId?: number;
  minMentorHours?: number;
  maxMentorHours?: number;
  minExperience?: number;
  maxExperience?: number;
  location?: string;
  /** createdAt | rating | name */
  sortBy?: string;
  /** asc | desc */
  sortOrder?: string;
}

export interface MenteeMentorHoursItem {
  mentorName: string;
  totalHours: number;
}

export interface MenteeSearchResponse {
  id: number;
  firstName: string;
  lastName: string;
  title?: string;
  bio?: string;
  location?: string;
  experienceYears?: number;
  category?: string;
  email: string;
  phone?: string;
  website?: string;
  linkedIn?: string;
  github?: string;
  twitter?: string;
  profileImage?: string;
  skills: string[];
  rating?: number;
  reviewCount?: number;
  createdAt: string;
  /** Hangi mentör ile kaç saat (onaylanmış/tamamlanmış) */
  mentorHours?: MenteeMentorHoursItem[];
  /** Katıldığı bootcampler */
  bootcampTitles?: string[];
  /** Yaptığı projeler */
  projectNames?: string[];
  /** Referans veren mentörler */
  referencedByMentors?: { id: number; firstName: string; lastName: string }[];
}

export interface MenteeSearchFilters {
  categories: string[];
  skills: string[];
  minExperience: number;
  maxExperience: number;
}

export interface MenteeDetailResponse {
  id: number;
  firstName: string;
  lastName: string;
  title?: string;
  bio?: string;
  experienceYears?: number;
  category?: string;
  email: string;
  phone?: string;
  website?: string;
  linkedIn?: string;
  github?: string;
  twitter?: string;
  profileImage?: string;
  skills?: MenteeSkillResponse[];
  educations?: MenteeEducationResponse[];
  workExperiences?: MenteeWorkExperienceResponse[];
  projects?: MenteeProjectResponse[];
  certificates?: MenteeCertificateResponse[];
  createdAt: string;
  referencedByMentors?: { id: number; firstName: string; lastName: string }[];
}

// Company Types
export interface CompanyProfileResponse {
  id: number;
  companyName: string;
  address?: string;
  taxNumber: string;
  authorizedPerson: string;
  phoneNumber: string;
  email: string;
  website?: string;
  companySize?: string;
  industry?: string;
  logo?: string; // Base64 encoded logo
  isApproved: boolean;
  approvalDate?: string;
  subscriptionType: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  isSubscriptionActive: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface UpdateCompanyProfileRequest {
  companyName: string;
  address?: string;
  taxNumber: string;
  authorizedPerson: string;
  phoneNumber: string;
  website?: string;
  companySize?: string;
  industry?: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangeEmailRequest {
  password: string;
  newEmail: string;
}

// Public Job Posting Types
export interface PublicJobPostingResponse {
  id: number;
  title: string;
  companyName: string;
  companyLogo?: string;
  location: string;
  department: string;
  employmentType: string;
  experienceLevel?: string;
  minExperienceYears?: number;
  maxExperienceYears?: number;
  salaryMin?: number;
  salaryMax?: number;
  applicationDeadline: string;
  createdAt: string;
  categoryId?: number;
  categoryName?: string;
}

export interface PublicJobPostingDetailResponse {
  id: number;
  title: string;
  description: string;
  requirements: string;
  benefits?: string;
  location: string;
  department: string;
  employmentType: string;
  experienceLevel?: string;
  minExperienceYears?: number;
  maxExperienceYears?: number;
  salaryMin?: number;
  salaryMax?: number;
  applicationDeadline: string;
  createdAt: string;
  companyName: string;
  companyLogo?: string;
  companyDescription?: string;
  companyWebsite?: string;
  companyIndustry?: string;
  companySize?: string;
  categoryId?: number;
  categoryName?: string;
}

export interface PublicJobPostingSearchRequest {
  page: number;
  pageSize: number;
  searchTerm?: string;
  minExperience?: number;
  maxExperience?: number;
  minSalary?: number;
  maxSalary?: number;
  category?: string;
  sortBy?: string;
  sortOrder?: string;
}

export interface JobPostingFiltersResponse {
  categories: string[];
  minSalary?: number;
  maxSalary?: number;
}

export interface JobApplicationRequest {
  CoverLetter: string;
}


// Job Posting Types
export interface JobPostingResponse {
  id: number;
  title: string;
  description?: string;
  salaryMin?: number;
  salaryMax?: number;
  experienceLevel?: string;
  minExperienceYears?: number;
  maxExperienceYears?: number;
  requirements?: string;
  benefits?: string;
  applicationDeadline: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  applicationCount: number;
  categoryId?: number;
  categoryName?: string;
}

export interface CreateJobPostingRequest {
  title: string;
  description?: string;
  salaryMin?: number;
  salaryMax?: number;
  experienceLevel?: string;
  minExperienceYears?: number;
  maxExperienceYears?: number;
  requirements?: string;
  benefits?: string;
  applicationDeadline: string;
  isActive?: boolean;
  categoryId?: number;
}

export interface UpdateJobPostingRequest {
  title: string;
  description?: string;
  salaryMin?: number;
  salaryMax?: number;
  experienceLevel?: string;
  minExperienceYears?: number;
  maxExperienceYears?: number;
  requirements?: string;
  benefits?: string;
  applicationDeadline: string;
  isActive: boolean;
}

export interface MentorWorkInfo {
  mentorId: number;
  mentorName: string;
  hours: number;
  completedSessions: number;
}

export interface CandidateFilterRequest {
  searchTerm?: string;
  skill?: string;
  approvedSkill?: string; // Onaylanmış yetkinlik
  project?: string;
  bootcampTopic?: string;
  mentorId?: number; // Belirli bir mentör ile çalışmış
  minMentorHours?: number;
  maxMentorHours?: number;
  minExperience?: number;
  maxExperience?: number;
  location?: string;
  status?: string; // Pool için
}

export interface JobApplicationResponse {
  id: number;
  coverLetter?: string;
  resumeFileName?: string;
  resumeFileSize?: number;
  status: string;
  adminNotes?: string;
  appliedAt: string;
  reviewedAt?: string;
  jobPostingId: number;
  jobTitle: string;
  companyName?: string;
  menteeId?: number;
  mentorId?: number;
  applicantName?: string;
  applicantEmail?: string;
  applicantType?: string;
  // Detaylı bilgiler
  skills?: string[];
  approvedSkills?: string[];
  projects?: string[];
  totalMentorHours?: number;
  mentorWorks?: MentorWorkInfo[];
  bootcampTopics?: string[];
  latestInterview?: {
    id: number;
    interviewDate: string;
    interviewType: string;
    status: string;
    interviewerName?: string;
    location?: string;
    notes?: string;
  } | null;
}

export interface UpdateJobApplicationRequest {
  status: string;
  adminNotes?: string;
}

export interface InterviewResponse {
  id: number;
  interviewDate: string;
  interviewType: string;
  status: string;
  notes?: string;
  interviewerName?: string;
  interviewFeedback?: string;
  rating?: number;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  jobApplicationId: number;
  applicantName?: string;
  jobTitle?: string;
  companyName?: string;
  location?: string;
}

export interface CreateInterviewRequest {
  interviewDate: string;
  interviewType: string;
  notes?: string;
  interviewerName?: string;
  jobApplicationId: number;
}

export interface UpdateInterviewRequest {
  interviewDate: string;
  interviewType: string;
  status: string;
  notes?: string;
  interviewerName?: string;
  interviewFeedback?: string;
  rating?: number;
}

// Candidate Management Types
export interface CandidateFavoriteResponse {
  id: number;
  companyId: number;
  menteeId?: number;
  mentorId?: number;
  applicantName: string;
  applicantEmail: string;
  applicantType: string;
  notes: string;
  createdAt: string;
  // Detaylı bilgiler
  skills?: string[];
  approvedSkills?: string[];
  projects?: string[];
  totalMentorHours?: number;
  mentorWorks?: MentorWorkInfo[];
  bootcampTopics?: string[];
}

export interface CandidatePoolResponse {
  id: number;
  companyId: number;
  menteeId?: number;
  mentorId?: number;
  applicantName: string;
  applicantEmail: string;
  applicantType: string;
  notes: string;
  status: string;
  createdAt: string;
  // Detaylı bilgiler
  skills?: string[];
  approvedSkills?: string[];
  projects?: string[];
  totalMentorHours?: number;
  mentorWorks?: MentorWorkInfo[];
  bootcampTopics?: string[];
}

export interface AddToFavoritesRequest {
  menteeId?: number;
  mentorId?: number;
  notes: string;
}

export interface AddToPoolRequest {
  menteeId?: number;
  mentorId?: number;
  notes: string;
  status: string;
}

export interface UpdatePoolStatusRequest {
  status: string;
  notes: string;
}

// Admin Company Types
export interface AdminCompanyResponse {
  id: number;
  companyName: string;
  email: string;
  phoneNumber: string;
  address?: string;
  taxNumber: string;
  authorizedPerson: string;
  website?: string;
  companySize?: string;
  industry?: string;
  description?: string;
  logo?: string;
  firmStatus: number; // 0: Pending, 1: Approved, 2: Rejected
  approvalDate?: string;
  subscriptionType: number; // 0: Free, 1: Monthly, 2: Yearly
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  isSubscriptionActive: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface UpdateAdminCompanyRequest {
  companyName: string;
  address?: string;
  taxNumber: string;
  authorizedPerson: string;
  phoneNumber: string;
  website?: string;
  companySize?: string;
  industry?: string;
  description?: string;
  subscriptionType: number;
  isSubscriptionActive: boolean;
  subscriptionEndDate?: string;
}

// Mentee Detail Types - Already defined above

// Advertisement Types
export enum AdvertisementType {
  Banner = 1,
  Card = 2
}

export enum AdvertisementPosition {
  Top = 0,
  Inline = 1,
  Bottom = 2,
  Sidebar = 3
}

export enum AdvertisementRequestStatus {
  Pending = 1,
  Approved = 2,
  Rejected = 3
}

/** Müsait gün satırı (API: week* alan adları tarihsel) */
export interface WeekAvailabilityInfo {
  weekStartDate: string;
  weekEndDate: string;
  isAvailable: boolean;
  weekLabel: string;
}

export interface AdvertisementSlotResponse {
  id: number;
  type: AdvertisementType;
  typeName: string;
  position: AdvertisementPosition;
  positionName: string;
  name: string;
  description?: string;
  pagePath: string;
  price: number;
  isActive: boolean;
  isAvailable: boolean;
  availableWeeks: WeekAvailabilityInfo[];
  createdAt: string;
}

export interface AdvertisementRequestResponse {
  id: number;
  mentorId: number;
  mentorName: string;
  advertisementSlotId: number;
  slotName: string;
  slotType: AdvertisementType;
  slotPosition: AdvertisementPosition;
  slotPositionName: string;
  imageBase64: string;
  imageContentType?: string;
  linkUrl?: string;
  title?: string;
  description?: string;
  status: AdvertisementRequestStatus;
  statusName: string;
  rejectionReason?: string;
  requestedWeekStartDate: string;
  requestedWeekLabel: string;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  approvedAt?: string;
  paymentAmount: number;
  paymentTransactionId?: string;
  paymentStatus: string;
}

export interface AdvertisementResponse {
  id: number;
  mentorId: number;
  advertisementSlotId: number;
  type: AdvertisementType;
  position: AdvertisementPosition;
  imageBase64: string;
  imageContentType?: string;
  linkUrl?: string;
  title?: string;
  description?: string;
  startDate: string;
  endDate: string;
}

export interface CreateAdvertisementRequestRequest {
  advertisementSlotId: number;
  image: File;
  requestedWeekStartDate: string; // YYYY-MM-DD yayın günü
  linkUrl?: string;
  title?: string;
  description?: string;
}

// Subscription Types
export enum SubscriptionType {
  Free = 0,
  Monthly = 1,
  Yearly = 2
}

export interface SubscriptionResponse {
  id: number;
  subscriptionType: SubscriptionType;
  startDate: string;
  endDate: string;
  amount: number;
  paymentStatus: string;
  isActive: boolean;
  createdAt: string;
}

export interface CurrentSubscriptionResponse {
  subscriptionType: SubscriptionType;
  startDate?: string;
  endDate?: string;
  isActive: boolean;
  daysRemaining: number;
  isExpired: boolean;
}

export interface CreateSubscriptionRequest {
  subscriptionType: SubscriptionType;
  cardNumber: string;
  cardHolderName: string;
  expiryMonth: string; // MM formatında
  expiryYear: string; // YY formatında
  cvv: string;
  phoneNumber?: string;
}

export interface RenewSubscriptionRequest {
  subscriptionType: SubscriptionType;
  cardNumber: string;
  cardHolderName: string;
  expiryMonth: string; // MM formatında
  expiryYear: string; // YY formatında
  cvv: string;
  phoneNumber?: string;
}

export interface ParamPaymentInitResponse {
  success: boolean;
  message: string;
  paymentUrl?: string;
  orderId?: string;
}

export interface FakePaymentRequest {
  amount: number;
  paymentMethod: string;
  cardNumber?: string;
  cardHolderName?: string;
  expiryDate?: string;
  cvv?: string;
}

export interface FakePaymentResponse {
  success: boolean;
  transactionId: string;
  message: string;
}

/** GET /api/subscription/dev/fake-payment-enabled */
export interface DevFakePaymentFeatureResponse {
  enabled: boolean;
}

/** POST /api/subscription/dev/fake-complete-subscription */
export interface DevFakeSubscriptionCompleteRequest {
  subscriptionType: SubscriptionType;
  isRenewal: boolean;
}

/** POST /api/mentor/advertisements/dev/fake-complete-request */
export interface DevFakeAdvertisementResponse {
  requestId: number;
  orderId: string;
  message: string;
}

// Admin Subscription Types
export interface AdminSubscriptionResponse {
  id: number;
  userId: number;
  userType: string;
  userName?: string;
  userEmail?: string;
  subscriptionType: SubscriptionType;
  startDate: string;
  endDate: string;
  amount: number;
  paymentTransactionId?: string;
  paymentStatus: string;
  isActive: boolean;
  createdAt: string;
  cancelledAt?: string;
}

export interface AdminManageSubscriptionRequest {
  userId: number;
  userType: string;
  subscriptionType: SubscriptionType;
  startDate?: string;
  endDate?: string;
  amount?: number;
}

export interface SubscriptionPricingResponse {
  monthlyPrice: number;
  yearlyPrice: number;
  updatedAt: string;
}

export interface UpdateSubscriptionPricingRequest {
  monthlyPrice: number;
  yearlyPrice: number;
}

export interface NotificationItem {
  id: number;
  title: string;
  message?: string;
  isRead: boolean;
  createdAt: string;
  type?: string;
  relatedId?: number;
  linkUrl?: string;
}
